"use client";

import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import Image from "next/image";
import { useQueryStates } from "nuqs";
import { trpc } from "@/lib/trpc/client";
import { formatCatalogCode, formatPrice } from "@/lib/utils";
import { Card } from "@/components/ui/Card";
import { Pagination, PaginationInfo } from "@/components/ui/Pagination";
import { Skeleton } from "@/components/ui/Skeleton";
import { Lightbox } from "@/components/ui/Modal";
import {
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
} from "nuqs";
import { Search, Image as ImageIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

interface LightboxPhotoWithMeta {
  url: string;
  thumbnailUrl: string;
  alt: string;
  filename: string;
  caption: string;
  subcaption?: string;
  paintingId: number;
  pageNumber: number;
}

const PAGE_SIZE = 24;

const sortByOptions = ["id", "artistName", "year"] as const;
const sortOrderOptions = ["asc", "desc"] as const;

const gallerySearchParams = {
  page: parseAsInteger.withDefault(1),
  search: parseAsString.withDefault(""),
  sortBy: parseAsStringLiteral(sortByOptions).withDefault("id"),
  sortOrder: parseAsStringLiteral(sortOrderOptions).withDefault("asc"),
  itemType: parseAsString,
};

interface PublicPaintingCardProps {
  painting: {
    id: number;
    catalogNumber: number;
    itemType: string;
    title: string | null;
    artistName: string | null;
    artistAge: number | null;
    price: string | null;
    primaryPhotoUrl: string | null;
    isSold: boolean;
  };
  onClick: () => void;
}

function PublicPaintingCard({ painting, onClick, t }: PublicPaintingCardProps & { t: (key: string) => string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group block w-full text-left rounded-lg border border-gray-200 bg-white overflow-hidden hover:shadow-lg hover:border-gray-300 transition-all focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
    >
      <div className="aspect-square relative bg-gray-100">
        {painting.primaryPhotoUrl ? (
          <Image
            src={painting.primaryPhotoUrl}
            alt={painting.title || "Item"}
            fill
            className="object-contain group-hover:scale-105 transition-transform duration-300"
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <ImageIcon className="h-12 w-12 text-gray-300" />
          </div>
        )}
        {painting.isSold && (
          <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
            {t("gallery.sold")}
          </div>
        )}
      </div>
      <div className="p-3">
        <p className="text-xs text-primary-800 font-mono font-medium">
          {formatCatalogCode(painting.itemType, painting.catalogNumber)}
        </p>
        <p className="font-medium text-gray-900 truncate mt-0.5">
          {painting.title || <span className="text-gray-400 italic">{t("gallery.untitled")}</span>}
        </p>
        <p className="text-sm text-gray-500 truncate">
          {painting.artistName || <span className="text-gray-400">{t("gallery.unknownArtist")}</span>}
          {painting.artistAge && (
            <span className="text-gray-400"> ({painting.artistAge})</span>
          )}
        </p>
        {formatPrice(painting.price) && (
          <p className="text-sm font-medium text-gray-900 mt-1">{formatPrice(painting.price)}</p>
        )}
      </div>
    </button>
  );
}

export function PublicGallery() {
  const { t } = useTranslation();
  const [params, setParams] = useQueryStates(gallerySearchParams, {
    shallow: false,
  });

  const { page, search, sortBy, sortOrder, itemType } = params;

  // Item types for the filter dropdown (from the reference table)
  const itemTypesQuery = trpc.itemTypes.list.useQuery();

  // Local state for search input (for immediate feedback while typing)
  const [searchInput, setSearchInput] = useState(search);

  // Lightbox state for gallery viewing with infinite loading
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxPhotos, setLightboxPhotos] = useState<LightboxPhotoWithMeta[]>([]);
  const [loadedPages, setLoadedPages] = useState<Set<number>>(new Set());
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const lightboxStartPageRef = useRef(1);

  // tRPC utils for manual fetching
  const trpcUtils = trpc.useUtils();

  // Sync search input with URL when navigating back/forward
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  // Debounced search update to URL
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== search) {
        setParams({ search: searchInput || null, page: 1 });
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchInput, search, setParams]);

  const paintingsQuery = trpc.paintings.publicList.useQuery({
    page,
    limit: PAGE_SIZE,
    search: search || undefined,
    sortBy,
    sortOrder,
    itemType: itemType ?? undefined,
  });

  const paintings = useMemo(
    () => paintingsQuery.data?.paintings ?? [],
    [paintingsQuery.data?.paintings]
  );
  const total = paintingsQuery.data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Convert paintings to lightbox photo format
  const paintingsToLightboxPhotos = useCallback(
    (
      paintingsList: typeof paintings,
      pageNum: number
    ): LightboxPhotoWithMeta[] => {
      return paintingsList
        .filter((p) => p.primaryPhotoUrl)
        .map((painting) => ({
          // Use full-res URL for lightbox, fall back to thumbnail if not available
          url: painting.primaryPhotoFullUrl || painting.primaryPhotoUrl!,
          thumbnailUrl: painting.primaryPhotoUrl!,
          alt: painting.title || "Item",
          filename: `${formatCatalogCode(painting.itemType, painting.catalogNumber)}.jpg`,
          caption: `${formatCatalogCode(painting.itemType, painting.catalogNumber)}${painting.title ? ` - ${painting.title}` : ""}`,
          subcaption: painting.artistName
            ? `${painting.artistName}${painting.artistAge ? ` (${painting.artistAge})` : ""}`
            : undefined,
          paintingId: painting.id,
          pageNumber: pageNum,
        }));
    },
    []
  );

  // Prepare current page's lightbox photos for mapping clicks
  const currentPagePhotos = paintingsToLightboxPhotos(paintings, page);

  // Map from painting ID to index in current page photos (for click handling)
  const paintingIdToCurrentPageIndex = new Map<number, number>();
  paintings.forEach((painting, idx) => {
    if (painting.primaryPhotoUrl) {
      const photoIdx = currentPagePhotos.findIndex(
        (p) => p.paintingId === painting.id
      );
      if (photoIdx !== -1) {
        paintingIdToCurrentPageIndex.set(painting.id, idx);
      }
    }
  });

  // Load additional page for lightbox
  const loadPage = useCallback(
    async (pageNum: number, position: "before" | "after") => {
      if (loadedPages.has(pageNum) || pageNum < 1 || pageNum > totalPages) {
        return;
      }

      setIsLoadingMore(true);
      try {
        const data = await trpcUtils.paintings.publicList.fetch({
          page: pageNum,
          limit: PAGE_SIZE,
          search: search || undefined,
          sortBy,
          sortOrder,
          itemType: itemType ?? undefined,
        });

        const newPhotos = paintingsToLightboxPhotos(data.paintings, pageNum);

        setLightboxPhotos((prev) => {
          if (position === "before") {
            return [...newPhotos, ...prev];
          } else {
            return [...prev, ...newPhotos];
          }
        });

        setLoadedPages((prev) => new Set([...prev, pageNum]));

        // If we added photos before, adjust the index to keep the same photo visible
        if (position === "before") {
          setLightboxIndex((prev) => prev + newPhotos.length);
        }
      } finally {
        setIsLoadingMore(false);
      }
    },
    [
      loadedPages,
      totalPages,
      trpcUtils.paintings.publicList,
      search,
      sortBy,
      sortOrder,
      itemType,
      paintingsToLightboxPhotos,
    ]
  );

  // Handle lightbox navigation with infinite loading
  const handleLightboxNavigate = useCallback(
    (newIndex: number) => {
      // Check if we need to load more pages
      const minLoadedPage = Math.min(...loadedPages);
      const maxLoadedPage = Math.max(...loadedPages);

      // Navigating forward - check if we need next page
      if (newIndex > lightboxIndex && newIndex >= lightboxPhotos.length - 1) {
        if (maxLoadedPage < totalPages && !isLoadingMore) {
          loadPage(maxLoadedPage + 1, "after");
        }
      }

      // Navigating backward - check if we need previous page
      if (newIndex < lightboxIndex && newIndex <= 0) {
        if (minLoadedPage > 1 && !isLoadingMore) {
          loadPage(minLoadedPage - 1, "before");
        }
      }

      // Allow navigation within bounds
      if (newIndex >= 0 && newIndex < lightboxPhotos.length) {
        setLightboxIndex(newIndex);
      }
    },
    [lightboxPhotos, lightboxIndex, loadedPages, totalPages, isLoadingMore, loadPage]
  );

  // Open lightbox at a specific painting
  const openLightbox = useCallback(
    (paintingIndex: number) => {
      const painting = paintings[paintingIndex];
      if (!painting?.primaryPhotoUrl) return;

      // Initialize lightbox with current page's photos
      const photos = paintingsToLightboxPhotos(paintings, page);
      setLightboxPhotos(photos);
      setLoadedPages(new Set([page]));
      lightboxStartPageRef.current = page;

      // Find the index of the clicked painting in the photos array
      const photoIndex = photos.findIndex(
        (p) => p.paintingId === painting.id
      );
      setLightboxIndex(photoIndex >= 0 ? photoIndex : 0);
      setLightboxOpen(true);
    },
    [paintings, page, paintingsToLightboxPhotos]
  );

  // Close lightbox and reset state
  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    // Reset after animation
    setTimeout(() => {
      setLightboxPhotos([]);
      setLoadedPages(new Set());
      setLightboxIndex(0);
    }, 300);
  }, []);

  const handlePageChange = (newPage: number) => {
    setParams({ page: newPage });
  };

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-2xl font-bold text-gray-900">
            {t("gallery.title")}
          </h1>
          <p className="text-gray-500 mt-1">
            {t("gallery.browseCollection", { count: total })}
          </p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Card className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t("gallery.searchPlaceholder")}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="flex gap-2">
              {(itemTypesQuery.data?.length ?? 0) > 1 && (
                <select
                  value={itemType ?? ""}
                  onChange={(e) =>
                    setParams({
                      itemType: e.target.value || null,
                      page: 1,
                    })
                  }
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">{t("gallery.allTypes")}</option>
                  {(itemTypesQuery.data ?? []).map((type) => (
                    <option key={type.slug} value={type.slug}>
                      {type.name}
                    </option>
                  ))}
                </select>
              )}
              <select
                value={sortBy}
                onChange={(e) =>
                  setParams({
                    sortBy: e.target.value as typeof sortBy,
                    page: 1,
                  })
                }
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="id">{t("gallery.sortById")}</option>
                <option value="artistName">{t("gallery.sortByArtist")}</option>
                <option value="year">{t("gallery.sortByYear")}</option>
              </select>
              <select
                value={sortOrder}
                onChange={(e) =>
                  setParams({
                    sortOrder: e.target.value as typeof sortOrder,
                    page: 1,
                  })
                }
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="asc">{t("gallery.ascending")}</option>
                <option value="desc">{t("gallery.descending")}</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Content */}
        {paintingsQuery.isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="rounded-lg border border-gray-200 overflow-hidden bg-white">
                <Skeleton className="aspect-square w-full" />
                <div className="p-3 space-y-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : paintings.length === 0 ? (
          <Card className="text-center py-12">
            <ImageIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">{t("gallery.noResults")}</h3>
            <p className="text-gray-500 mt-1">
              {search
                ? t("gallery.noSearchResults", { search })
                : t("gallery.emptyGallery")}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {paintings.map((painting, index) => (
              <PublicPaintingCard
                key={painting.id}
                painting={painting}
                onClick={() => openLightbox(index)}
                t={t}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <PaginationInfo
              currentPage={page}
              pageSize={PAGE_SIZE}
              totalItems={total}
            />
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/* Gallery Lightbox */}
      <Lightbox
        photos={lightboxPhotos}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={closeLightbox}
        onNavigate={handleLightboxNavigate}
        totalCount={total}
        globalIndex={
          lightboxPhotos.length > 0
            ? (Math.min(...loadedPages) - 1) * PAGE_SIZE + lightboxIndex
            : 0
        }
        isLoading={isLoadingMore}
        showDownload={false}
      />
    </div>
  );
}
